import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import GuideLead from '../components/GuideLead';
import GuideCTA from '../components/GuideCTA';

const GuideEtudiantCV = () => {
    return (
        <div className="min-h-screen bg-slate-200">
            <Helmet>
                <title>CV Étudiant : Guide Complet pour Première Candidature (Sans Expérience)</title>
                <meta name="description" content="Guide complet pour créer un CV étudiant sans expérience : structure, valoriser stages et projets, compétences à mettre en avant, exemples par filière et erreurs à éviter. Modèles gratuits." />
                <meta name="keywords" content="CV étudiant, CV sans expérience, CV première candidature, CV stage alternance, CV jeune diplômé, comment faire un CV sans expérience, modèle CV étudiant, CV étudiant exemple" />
                <link rel="canonical" href="https://www.cv-analyse.fr/guide/cv-etudiant" />
                <script type="application/ld+json">{`
                {
                  "@context": "https://schema.org",
                  "@type": "BreadcrumbList",
                  "itemListElement": [
                    {"@type": "ListItem","position": 1,"name": "Accueil","item": "https://www.cv-analyse.fr/"},
                    {"@type": "ListItem","position": 2,"name": "CV étudiant","item": "https://www.cv-analyse.fr/guide/cv-etudiant"}
                  ]
                }
                `}</script>
                <script type="application/ld+json">{`
                {
                  "@context": "https://schema.org",
                  "@type": "Article",
                  "headline": "CV Étudiant : Guide Complet pour Première Candidature",
                  "description": "Guide complet pour créer un CV étudiant sans expérience professionnelle",
                  "author": {"@type": "Organization", "name": "CV Analyse", "alternateName": "CV Analyzer"},
                  "publisher": {"@type": "Organization", "name": "CV Analyse", "alternateName": "CV Analyzer"},
                  "dateModified": "2026-08-15",
                  "datePublished": "2024-01-01",
                  "dateModified": "2024-01-01",
                  "mainEntityOfPage": {"@type": "WebPage", "@id": "https://www.cv-analyse.fr/guide/cv-etudiant"}
                }
                `}</script>
                <script type="application/ld+json">{`
                {
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  "mainEntity": [
                    {
                      "@type": "Question",
                      "name": "Comment faire un CV étudiant sans expérience ?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Valorisez vos stages, projets académiques, compétences acquises en cours, activités associatives et formations. Mettez l'accent sur vos soft skills et votre motivation."
                      }
                    },
                    {
                      "@type": "Question",
                      "name": "Quelle structure pour un CV étudiant ?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Structure recommandée : En-tête avec coordonnées, Profil/Objectif professionnel, Formation, Expériences (stages, jobs étudiants), Projets, Compétences, Langues et Centres d'intérêt."
                      }
                    },
                    {
                      "@type": "Question",
                      "name": "Un CV étudiant doit-il faire 1 page ?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Oui, pour un étudiant ou jeune diplômé, un CV d'une page est recommandé. Concentrez-vous sur l'essentiel et valorisez vos atouts même sans expérience professionnelle longue."
                      }
                    }
                  ]
                }
                `}</script>
                <script type="application/ld+json">{`
                {
                  "@context": "https://schema.org",
                  "@type": "HowTo",
                  "name": "Comment créer un CV étudiant efficace",
                  "description": "Construisez un CV étudiant percutant même sans expérience professionnelle en valorisant vos atouts académiques et personnels.",
                  "step": [
                    {"@type": "HowToStep", "name": "Choisir le bon format", "text": "Optez pour un format chronologique inversé sur 1 page maximum pour un profil étudiant."},
                    {"@type": "HowToStep", "name": "Mettre en avant sa formation", "text": "Listez vos diplômes, école, spécialisation et mentions obtenues en tête de CV."},
                    {"@type": "HowToStep", "name": "Valoriser stages, jobs étudiants et projets", "text": "Décrivez chaque expérience avec des verbes d'action et des résultats concrets si possible."},
                    {"@type": "HowToStep", "name": "Ajouter compétences et soft skills", "text": "Langues, outils informatiques, compétences relationnelles acquises via les associations ou le sport."},
                    {"@type": "HowToStep", "name": "Analyser son CV avec un outil IA", "text": "Utilisez CV Analyse pour optimiser votre score et passer les filtres ATS dès votre premier emploi."}
                  ]
                }
                `}</script>
            </Helmet>

            {/* Fil d'Ariane */}
            <nav aria-label="Fil d'Ariane" className="pt-16 pb-4">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <ol className="flex items-center space-x-2 text-sm text-slate-600" itemScope itemType="https://schema.org/BreadcrumbList">
                        <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                            <Link to="/" itemProp="item" className="hover:text-slate-900">
                                <span itemProp="name">Accueil</span>
                            </Link>
                            <meta itemProp="position" content="1" />
                        </li>
                        <li className="text-slate-400">/</li>
                        <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                            <span itemProp="name" className="text-slate-900">CV étudiant</span>
                            <meta itemProp="position" content="2" />
                        </li>
                    </ol>
                </div>
            </nav>

            {/* Hero Section */}
            <div className="bg-slate-900 pt-4 pb-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 rounded-3xl mb-6">
                            <span className="text-4xl">🎓</span>
                        </div>
                        <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                            CV Étudiant : Guide Complet pour Première Candidature
                        </h1>
                        <p className="text-xl md:text-2xl text-white max-w-3xl mx-auto leading-relaxed font-medium">
                            Créez un CV percutant même sans expérience professionnelle : valorisez vos stages, projets et compétences
                        </p>
                    </div>
                </div>
            </div>

            <GuideLead slug="cv-etudiant" />

            {/* Table des matières */}
            <div className="card-surface mx-4 mt-4 max-w-4xl mx-auto">
                <div className="p-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-4">📋 Table des matières</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <a href="#structure" className="flex items-center p-3 rounded-xl hover:bg-indigo-50 transition-colors">
                            <span className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center mr-3 text-indigo-600 font-bold">1</span>
                            <span className="text-gray-700">Structure du CV étudiant</span>
                        </a>
                        <a href="#valoriser" className="flex items-center p-3 rounded-xl hover:bg-indigo-50 transition-colors">
                            <span className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center mr-3 text-indigo-600 font-bold">2</span>
                            <span className="text-gray-700">Valoriser sans expérience</span>
                        </a>
                        <a href="#competences" className="flex items-center p-3 rounded-xl hover:bg-indigo-50 transition-colors">
                            <span className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center mr-3 text-indigo-600 font-bold">3</span>
                            <span className="text-gray-700">Compétences à mettre en avant</span>
                        </a>
                        <a href="#exemples" className="flex items-center p-3 rounded-xl hover:bg-indigo-50 transition-colors">
                            <span className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center mr-3 text-indigo-600 font-bold">4</span>
                            <span className="text-gray-700">Exemples par filière</span>
                        </a>
                        <a href="#erreurs" className="flex items-center p-3 rounded-xl hover:bg-indigo-50 transition-colors">
                            <span className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center mr-3 text-indigo-600 font-bold">5</span>
                            <span className="text-gray-700">Erreurs à éviter</span>
                        </a>
                        <a href="#checklist" className="flex items-center p-3 rounded-xl hover:bg-indigo-50 transition-colors">
                            <span className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center mr-3 text-indigo-600 font-bold">6</span>
                            <span className="text-gray-700">Check-list finale</span>
                        </a>
                    </div>
                </div>
            </div>

            <article className="px-6 py-16 mx-auto max-w-5xl">

                {/* Section 1: Structure */}
                <div id="structure" className="mb-16">
                    <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
                        <div className="flex items-center mb-6">
                            <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-2xl flex items-center justify-center mr-4">
                                <span className="text-2xl">📄</span>
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900">Structure du CV étudiant</h2>
                        </div>
                        
                        <div className="bg-gradient-to-r from-indigo-50 to-blue-100 rounded-2xl p-6 mb-6">
                            <h3 className="text-xl font-bold text-indigo-900 mb-3">💡 L'ordre des sections compte</h3>
                            <p className="text-gray-700">Pour un CV étudiant sans expérience, adaptez l'ordre pour mettre en valeur vos atouts.</p>
                        </div>
                        
                        <div className="space-y-4">
                            <div className="bg-white border-l-4 border-indigo-500 p-6 rounded-r-xl shadow-sm">
                                <h3 className="text-lg font-bold text-gray-900 mb-3">1. En-tête avec coordonnées</h3>
                                <ul className="text-gray-700 space-y-2 text-sm">
                                    <li>• Nom et prénom (en grand)</li>
                                    <li>• Téléphone et email professionnel</li>
                                    <li>• LinkedIn (si vous en avez un)</li>
                                    <li>• Ville (pas besoin de l'adresse complète)</li>
                                </ul>
                            </div>
                            
                            <div className="bg-white border-l-4 border-blue-500 p-6 rounded-r-xl shadow-sm">
                                <h3 className="text-lg font-bold text-gray-900 mb-3">2. Profil / Objectif professionnel (optionnel mais recommandé)</h3>
                                <p className="text-gray-700 text-sm mb-3">2-3 lignes qui présentent votre parcours et vos objectifs :</p>
                                <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-600">
                                    <p className="text-sm italic text-gray-700">
                                        "Étudiant en Master Marketing Digital, passionné par le e-commerce et les réseaux sociaux. 
                                        Recherche un stage de 6 mois pour mettre en pratique mes compétences en stratégie digitale 
                                        et contribuer à la croissance d'une entreprise innovante."
                                    </p>
                                </div>
                            </div>
                            
                            <div className="bg-white border-l-4 border-green-500 p-6 rounded-r-xl shadow-sm">
                                <h3 className="text-lg font-bold text-gray-900 mb-3">3. Formation (à mettre en premier si pas d'expérience)</h3>
                                <p className="text-gray-700 text-sm mb-3">Listez vos diplômes du plus récent au plus ancien :</p>
                                <div className="bg-green-50 p-4 rounded-lg">
                                    <div className="text-sm space-y-2">
                                        <div className="font-semibold text-gray-900">Master Marketing Digital</div>
                                        <div className="text-gray-600">Université Paris-Dauphine | 2022 - 2024</div>
                                        <div className="text-gray-600">Mention : Bien | Spécialisation : E-commerce</div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="bg-white border-l-4 border-purple-500 p-6 rounded-r-xl shadow-sm">
                                <h3 className="text-lg font-bold text-gray-900 mb-3">4. Expériences (stages, jobs étudiants, bénévolat)</h3>
                                <p className="text-gray-700 text-sm mb-3">Même un stage de 2 mois compte ! Valorisez tout :</p>
                                <ul className="text-gray-700 space-y-2 text-sm">
                                    <li>• Stages en entreprise</li>
                                    <li>• Jobs étudiants (serveur, vendeur, etc.)</li>
                                    <li>• Bénévolat et associations</li>
                                    <li>• Projets tutorés</li>
                                </ul>
                            </div>
                            
                            <div className="bg-white border-l-4 border-orange-500 p-6 rounded-r-xl shadow-sm">
                                <h3 className="text-lg font-bold text-gray-900 mb-3">5. Projets (très important pour les étudiants !)</h3>
                                <p className="text-gray-700 text-sm mb-3">Mettez en avant vos réalisations concrètes :</p>
                                <ul className="text-gray-700 space-y-2 text-sm">
                                    <li>• Projets universitaires (mémoires, études de cas)</li>
                                    <li>• Projets personnels (site web, blog, app mobile)</li>
                                    <li>• Concours et compétitions</li>
                                    <li>• Projets associatifs</li>
                                </ul>
                            </div>
                            
                            <div className="bg-white border-l-4 border-teal-500 p-6 rounded-r-xl shadow-sm">
                                <h3 className="text-lg font-bold text-gray-900 mb-3">6. Compétences</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                                    <div className="bg-teal-50 p-4 rounded-lg">
                                        <strong className="text-sm text-gray-900">Techniques :</strong>
                                        <p className="text-sm text-gray-600 mt-1">Excel, Photoshop, WordPress, Python, etc.</p>
                                    </div>
                                    <div className="bg-teal-50 p-4 rounded-lg">
                                        <strong className="text-sm text-gray-900">Soft skills :</strong>
                                        <p className="text-sm text-gray-600 mt-1">Communication, travail en équipe, autonomie, etc.</p>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="bg-white border-l-4 border-pink-500 p-6 rounded-r-xl shadow-sm">
                                <h3 className="text-lg font-bold text-gray-900 mb-3">7. Langues et Centres d'intérêt</h3>
                                <p className="text-gray-700 text-sm">Niveau de langue (A1 à C2) et passions qui peuvent être pertinentes</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Section 2: Valoriser sans expérience */}
                <div id="valoriser" className="mb-16">
                    <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
                        <div className="flex items-center mb-6">
                            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mr-4">
                                <span className="text-2xl">⭐</span>
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900">Comment valoriser sans expérience professionnelle ?</h2>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="bg-gradient-to-r from-blue-50 to-indigo-100 rounded-2xl p-6">
                                <h3 className="text-lg font-bold text-blue-900 mb-4">🎯 Valorisez vos stages</h3>
                                <div className="space-y-3 text-sm">
                                    <div className="bg-white p-4 rounded-lg">
                                        <strong className="text-gray-900">❌ À éviter :</strong>
                                        <p className="text-gray-600 mt-1">"Stage de 2 mois dans une entreprise"</p>
                                    </div>
                                    <div className="bg-white p-4 rounded-lg border-l-4 border-green-500">
                                        <strong className="text-gray-900">✅ À faire :</strong>
                                        <p className="text-gray-600 mt-1">"Stage Assistant Marketing Digital - Agence XYZ (2 mois)"</p>
                                        <ul className="text-gray-600 mt-2 space-y-1 ml-4">
                                            <li>• Rédaction de 15 articles de blog</li>
                                            <li>• Gestion des réseaux sociaux (+30% d'engagement)</li>
                                            <li>• Participation à la stratégie de contenu</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="bg-gradient-to-r from-green-50 to-emerald-100 rounded-2xl p-6">
                                <h3 className="text-lg font-bold text-green-900 mb-4">🚀 Mettez en avant vos projets</h3>
                                <div className="space-y-3 text-sm">
                                    <div className="bg-white p-4 rounded-lg border-l-4 border-green-500">
                                        <strong className="text-gray-900">Exemple :</strong>
                                        <p className="text-gray-600 mt-1 font-semibold">Projet : Site e-commerce</p>
                                        <ul className="text-gray-600 mt-2 space-y-1 ml-4">
                                            <li>• Création d'un site WordPress</li>
                                            <li>• Gestion de la base de données</li>
                                            <li>• 50 ventes en 3 mois</li>
                                        </ul>
                                    </div>
                                    <div className="bg-white p-4 rounded-lg border-l-4 border-blue-500">
                                        <strong className="text-gray-900">Exemple :</strong>
                                        <p className="text-gray-600 mt-1 font-semibold">Projet : Blog personnel</p>
                                        <ul className="text-gray-600 mt-2 space-y-1 ml-4">
                                            <li>• 30 articles publiés</li>
                                            <li>• 5 000 visiteurs/mois</li>
                                            <li>• SEO et réseaux sociaux</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="bg-gradient-to-r from-purple-50 to-pink-100 rounded-2xl p-6">
                                <h3 className="text-lg font-bold text-purple-900 mb-4">💼 Jobs étudiants = expérience</h3>
                                <p className="text-gray-700 text-sm mb-3">Un job étudiant développe des compétences précieuses :</p>
                                <div className="space-y-2 text-sm">
                                    <div className="bg-white p-3 rounded-lg">
                                        <strong>Serveur :</strong> Service client, gestion du stress, travail en équipe
                                    </div>
                                    <div className="bg-white p-3 rounded-lg">
                                        <strong>Vendeur :</strong> Relation client, négociation, objectifs commerciaux
                                    </div>
                                    <div className="bg-white p-3 rounded-lg">
                                        <strong>Baby-sitting :</strong> Responsabilité, autonomie, gestion du temps
                                    </div>
                                </div>
                            </div>
                            
                            <div className="bg-gradient-to-r from-orange-50 to-red-100 rounded-2xl p-6">
                                <h3 className="text-lg font-bold text-orange-900 mb-4">🎓 Bénévolat et associations</h3>
                                <p className="text-gray-700 text-sm mb-3">Très valorisant pour montrer votre engagement :</p>
                                <ul className="text-gray-700 space-y-2 text-sm">
                                    <li>• Président d'association étudiante</li>
                                    <li>• Bénévolat dans une ONG</li>
                                    <li>• Organisation d'événements</li>
                                    <li>• Tutorat et aide aux autres étudiants</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Section 3: Compétences */}
                <div id="competences" className="mb-16">
                    <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
                        <div className="flex items-center mb-6">
                            <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-2xl flex items-center justify-center mr-4">
                                <span className="text-2xl">🛠️</span>
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900">Compétences à mettre en avant</h2>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl p-6">
                                <h3 className="text-lg font-bold text-blue-900 mb-4">💻 Compétences techniques</h3>
                                <div className="space-y-2 text-sm">
                                    <div className="bg-white p-3 rounded-lg">
                                        <strong>Informatique :</strong> Office, Google Workspace, Canva
                                    </div>
                                    <div className="bg-white p-3 rounded-lg">
                                        <strong>Spécialisées :</strong> Photoshop, Excel avancé, WordPress
                                    </div>
                                    <div className="bg-white p-3 rounded-lg">
                                        <strong>Langages :</strong> Python, HTML/CSS, JavaScript (si pertinent)
                                    </div>
                                </div>
                            </div>
                            
                            <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-2xl p-6">
                                <h3 className="text-lg font-bold text-green-900 mb-4">🤝 Soft skills</h3>
                                <div className="space-y-2 text-sm">
                                    <div className="bg-white p-3 rounded-lg">
                                        <strong>Communication :</strong> Présentations, rédaction
                                    </div>
                                    <div className="bg-white p-3 rounded-lg">
                                        <strong>Travail d'équipe :</strong> Projets de groupe
                                    </div>
                                    <div className="bg-white p-3 rounded-lg">
                                        <strong>Autonomie :</strong> Gestion de projets personnels
                                    </div>
                                    <div className="bg-white p-3 rounded-lg">
                                        <strong>Adaptabilité :</strong> Capacité à apprendre rapidement
                                    </div>
                                </div>
                            </div>
                            
                            <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-2xl p-6">
                                <h3 className="text-lg font-bold text-purple-900 mb-4">🌍 Langues</h3>
                                <div className="space-y-2 text-sm">
                                    <div className="bg-white p-3 rounded-lg">
                                        <strong>Anglais :</strong> B2 (TOEIC 750) ou C1
                                    </div>
                                    <div className="bg-white p-3 rounded-lg">
                                        <strong>Espagnol :</strong> B1 (si pertinent)
                                    </div>
                                    <div className="bg-white p-3 rounded-lg">
                                        <strong>Autres :</strong> Mentionnez seulement si niveau correct
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div className="mt-6 bg-gradient-to-r from-indigo-50 to-blue-100 rounded-2xl p-6">
                            <h3 className="text-lg font-bold text-indigo-900 mb-3">💡 Conseil pro</h3>
                            <p className="text-gray-700 text-sm">
                                Adaptez vos compétences au poste visé. Si vous postulez pour un stage en marketing, 
                                mettez en avant Canva, Google Analytics, les réseaux sociaux. Si c'est pour un poste 
                                en développement, privilégiez les langages de programmation.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Section 4: Exemples par filière */}
                <div id="exemples" className="mb-16">
                    <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
                        <div className="flex items-center mb-6">
                            <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mr-4">
                                <span className="text-2xl">📚</span>
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900">Exemples concrets par filière</h2>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl p-6">
                                <h3 className="text-lg font-bold text-blue-900 mb-4">💻 Étudiant en Informatique</h3>
                                <div className="space-y-3 text-sm">
                                    <div className="bg-white p-4 rounded-lg">
                                        <strong className="text-gray-900">Projets :</strong>
                                        <ul className="text-gray-600 mt-1 ml-4 space-y-1">
                                            <li>• Application mobile React Native</li>
                                            <li>• Site e-commerce avec base de données</li>
                                            <li>• Contribution à un projet open source</li>
                                        </ul>
                                    </div>
                                    <div className="bg-white p-4 rounded-lg">
                                        <strong className="text-gray-900">Compétences :</strong>
                                        <p className="text-gray-600 mt-1">Python, JavaScript, React, Git, SQL</p>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-2xl p-6">
                                <h3 className="text-lg font-bold text-green-900 mb-4">📊 Étudiant en Marketing</h3>
                                <div className="space-y-3 text-sm">
                                    <div className="bg-white p-4 rounded-lg">
                                        <strong className="text-gray-900">Projets :</strong>
                                        <ul className="text-gray-600 mt-1 ml-4 space-y-1">
                                            <li>• Campagne Instagram pour une marque fictive</li>
                                            <li>• Blog avec 2000 visiteurs/mois</li>
                                            <li>• Étude de marché pour un projet étudiant</li>
                                        </ul>
                                    </div>
                                    <div className="bg-white p-4 rounded-lg">
                                        <strong className="text-gray-900">Compétences :</strong>
                                        <p className="text-gray-600 mt-1">Canva, Google Analytics, Facebook Ads, SEO</p>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-2xl p-6">
                                <h3 className="text-lg font-bold text-purple-900 mb-4">📝 Étudiant en Communication</h3>
                                <div className="space-y-3 text-sm">
                                    <div className="bg-white p-4 rounded-lg">
                                        <strong className="text-gray-900">Projets :</strong>
                                        <ul className="text-gray-600 mt-1 ml-4 space-y-1">
                                            <li>• Organisation d'un événement étudiant (200 personnes)</li>
                                            <li>• Création de contenu pour réseaux sociaux</li>
                                            <li>• Rédaction d'articles pour média étudiant</li>
                                        </ul>
                                    </div>
                                    <div className="bg-white p-4 rounded-lg">
                                        <strong className="text-gray-900">Compétences :</strong>
                                        <p className="text-gray-600 mt-1">Rédaction, Photoshop, Premiere Pro, Community Management</p>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-2xl p-6">
                                <h3 className="text-lg font-bold text-orange-900 mb-4">💼 Étudiant en Commerce</h3>
                                <div className="space-y-3 text-sm">
                                    <div className="bg-white p-4 rounded-lg">
                                        <strong className="text-gray-900">Expériences :</strong>
                                        <ul className="text-gray-600 mt-1 ml-4 space-y-1">
                                            <li>• Job étudiant : Vendeur (6 mois)</li>
                                            <li>• Stage : Assistant commercial</li>
                                            <li>• Projet : Business plan pour start-up</li>
                                        </ul>
                                    </div>
                                    <div className="bg-white p-4 rounded-lg">
                                        <strong className="text-gray-900">Compétences :</strong>
                                        <p className="text-gray-600 mt-1">Négociation, CRM, Excel avancé, Anglais B2</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Section 5: Erreurs à éviter */}
                <div id="erreurs" className="mb-16">
                    <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
                        <div className="flex items-center mb-6">
                            <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-red-600 rounded-2xl flex items-center justify-center mr-4">
                                <span className="text-2xl">⚠️</span>
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900">Erreurs à éviter absolument</h2>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                                <h3 className="text-lg font-bold text-red-900 mb-3">❌ CV trop long</h3>
                                <p className="text-gray-700 text-sm">Un CV étudiant doit faire 1 page maximum. Concentrez-vous sur l'essentiel.</p>
                            </div>
                            
                            <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                                <h3 className="text-lg font-bold text-red-900 mb-3">❌ Manque de précision</h3>
                                <p className="text-gray-700 text-sm">Évitez "Stage en entreprise". Précisez : "Stage Assistant Marketing - Agence XYZ (2 mois)".</p>
                            </div>
                            
                            <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                                <h3 className="text-lg font-bold text-red-900 mb-3">❌ Ne pas valoriser les projets</h3>
                                <p className="text-gray-700 text-sm">Vos projets personnels sont votre force ! Créez une section dédiée.</p>
                            </div>
                            
                            <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                                <h3 className="text-lg font-bold text-red-900 mb-3">❌ Compétences non adaptées</h3>
                                <p className="text-gray-700 text-sm">Ne listez pas toutes vos compétences. Adaptez au poste visé.</p>
                            </div>
                            
                            <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                                <h3 className="text-lg font-bold text-red-900 mb-3">❌ Fautes d'orthographe</h3>
                                <p className="text-gray-700 text-sm">Relisez plusieurs fois et faites relire. Une faute = candidature éliminée.</p>
                            </div>
                            
                            <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                                <h3 className="text-lg font-bold text-red-900 mb-3">❌ Email non professionnel</h3>
                                <p className="text-gray-700 text-sm">Évitez "superman123@email.com". Utilisez prénom.nom@email.com</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Section 6: Check-list */}
                <div id="checklist" className="mb-16">
                    <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
                        <div className="flex items-center mb-6">
                            <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-2xl flex items-center justify-center mr-4">
                                <span className="text-2xl">✅</span>
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900">Check-list finale avant envoi</h2>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-3">
                                <div className="flex items-start p-4 bg-green-50 rounded-xl">
                                    <input type="checkbox" className="mt-1 mr-3" defaultChecked disabled />
                                    <div>
                                        <strong className="text-gray-900">Format et présentation</strong>
                                        <ul className="text-sm text-gray-600 mt-1 ml-6 space-y-1">
                                            <li>• CV d'1 page maximum</li>
                                            <li>• Police lisible (Arial, Calibri, Times)</li>
                                            <li>• Mise en page aérée et professionnelle</li>
                                            <li>• Export en PDF (pas Word)</li>
                                        </ul>
                                    </div>
                                </div>
                                
                                <div className="flex items-start p-4 bg-green-50 rounded-xl">
                                    <input type="checkbox" className="mt-1 mr-3" defaultChecked disabled />
                                    <div>
                                        <strong className="text-gray-900">Contenu essentiel</strong>
                                        <ul className="text-sm text-gray-600 mt-1 ml-6 space-y-1">
                                            <li>• Coordonnées complètes et à jour</li>
                                            <li>• Formation détaillée avec dates</li>
                                            <li>• Stages et expériences valorisés</li>
                                            <li>• Projets mis en avant</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="space-y-3">
                                <div className="flex items-start p-4 bg-green-50 rounded-xl">
                                    <input type="checkbox" className="mt-1 mr-3" defaultChecked disabled />
                                    <div>
                                        <strong className="text-gray-900">Qualité du contenu</strong>
                                        <ul className="text-sm text-gray-600 mt-1 ml-6 space-y-1">
                                            <li>• Aucune faute d'orthographe</li>
                                            <li>• Phrases courtes et percutantes</li>
                                            <li>• Verbes d'action (créé, géré, développé)</li>
                                            <li>• Adapté au poste visé</li>
                                        </ul>
                                    </div>
                                </div>
                                
                                <div className="flex items-start p-4 bg-green-50 rounded-xl">
                                    <input type="checkbox" className="mt-1 mr-3" defaultChecked disabled />
                                    <div>
                                        <strong className="text-gray-900">Vérifications finales</strong>
                                        <ul className="text-sm text-gray-600 mt-1 ml-6 space-y-1">
                                            <li>• Relu par une autre personne</li>
                                            <li>• Testé sur différents appareils</li>
                                            <li>• Nom du fichier professionnel</li>
                                            <li>• Compatible ATS (si candidature en ligne)</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* CTA Section */}
                <GuideCTA
                    title="Premier CV ? Notre IA vous guide pas à pas"
                    description="Analyse spécialisée pour les profils sans expérience — stages, projets académiques, compétences. score et 3 conseils offerts."
                    ctaText="Analyser mon CV étudiant"
                    secondaryText="Créer mon CV"
                    secondaryLink="/generator"
                    theme="indigo"
                />

                {/* Navigation entre guides */}
                <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Link to="/guide/rediger-cv-percutant" className="group flex items-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                        <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mr-4 group-hover:bg-purple-100 transition-colors">
                            <svg className="w-6 h-6 text-gray-600 group-hover:text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-900 group-hover:text-purple-700">Guide précédent</h3>
                            <p className="text-sm text-gray-600">Rédiger un CV percutant</p>
                        </div>
                    </Link>
                    
                    <Link to="/guides" className="group flex items-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                        <div>
                            <h3 className="font-bold text-gray-900 group-hover:text-indigo-700">Retour aux guides</h3>
                            <p className="text-sm text-gray-600">Voir tous nos guides CV</p>
                        </div>
                        <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center ml-4 group-hover:bg-indigo-100 transition-colors">
                            <svg className="w-6 h-6 text-gray-600 group-hover:text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                            </svg>
                        </div>
                    </Link>
                </div>
            </article>
        </div>
    );
};

export default GuideEtudiantCV;

